import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Waveform, SignOut, SpotifyLogo, Play, Pause, SkipBack, SkipForward, MusicNotes,
  Heart, Playlist, CaretRight, ArrowsClockwise,
  SpeakerHigh, Sparkle, TrendUp, User, ThumbsUp, ThumbsDown,
  Shuffle, Repeat, RepeatOnce, DeviceMobile, ClockCounterClockwise, Broadcast, ImageSquare
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { API } from "../App";

const formatMs = (ms) => {
  if (ms == null || Number.isNaN(ms)) return "–:––";
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

export const DashboardPage = ({ auth }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [topArtists, setTopArtists] = useState([]);
  const [topTracks, setTopTracks] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [generatingRecs, setGeneratingRecs] = useState(false);
  const [timeRange, setTimeRange] = useState("medium_term");
  const [showAllArtists, setShowAllArtists] = useState(false);
  const [showAllTracks, setShowAllTracks] = useState(false);
  const [showAllPlaylists, setShowAllPlaylists] = useState(false);
  const [loadingMore, setLoadingMore] = useState(null);
  const [artistsTotal, setArtistsTotal] = useState(50);
  const [tracksTotal, setTracksTotal] = useState(50);
  const [playlistsTotal, setPlaylistsTotal] = useState(null);
  const [playlistsOffset, setPlaylistsOffset] = useState(0);

  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [libraryStats, setLibraryStats] = useState(null);
  const [libraryStatsError, setLibraryStatsError] = useState(null);

  const [playbackState, setPlaybackState] = useState(null);
  const [playbackLoading, setPlaybackLoading] = useState(false);
  const [playbackError, setPlaybackError] = useState(null);

  // Zakres uprawnien tokenu rozszerzono 2026-08-06 z 8 do 18 pozycji, ale sesje wydane
  // WCZESNIEJ nosza stary zakres — Spotify odpowiada wtedy "Insufficient client scope" /
  // "Permissions missing". Bez ponownego logowania (ekran zgody) sterowanie odtwarzaniem,
  // obserwowanie i zapis do biblioteki nie moga zadzialac, niezaleznie od kodu.
  const [scopeWarning, setScopeWarning] = useState(false);
  const isScopeError = (msg = "") => /insufficient client scope|permissions missing/i.test(msg);

  const handleApiError = (e, context) => {
    if (isScopeError(e.message)) {
      setScopeWarning(true);
      toast.error(`${context}: token nie ma wymaganych uprawnień — zaloguj się ponownie`);
    } else {
      toast.error(`${context}: ${e.message}`);
    }
  };

  // Auto-odswiezanie (2026-08-23). Pierwotnie bylo go CELOWO brak — po incydencie z limitem
  // zapytan Spotify (2026-08-06). Zasada, ktora to godzi: nigdy nie odpytujemy Spotify po to,
  // co mozemy policzyc lokalnie, i nigdy nie odpytujemy w tle niewidocznej karty.
  //   - pasek postepu     -> lokalny zegar, ZERO zapytan
  //   - dane z Mongo      -> co 60 s, zero zapytan do Spotify
  //   - stan odtwarzania  -> 1 zapytanie / 15 s, tylko gdy karta widoczna i cos gra
  // Do wylaczenia przelacznikiem w naglowku karty (zapamietywany w localStorage).
  const [autoRefresh, setAutoRefresh] = useState(
    () => localStorage.getItem("panspoti_auto_refresh") !== "off"
  );
  const [seekDrag, setSeekDrag] = useState(null);
  const [displayProgressMs, setDisplayProgressMs] = useState(null);

  useEffect(() => {
    localStorage.setItem("panspoti_auto_refresh", autoRefresh ? "on" : "off");
  }, [autoRefresh]);

  // `silent` = odswiezenie w tle: bez migania "Sprawdzam…" co kilkanascie sekund
  const fetchPlaybackState = async (silent = false) => {
    if (!silent) setPlaybackLoading(true);
    setPlaybackError(null);
    try {
      const res = await fetch(`${API}/spotify/playback-state?access_token=${auth.accessToken}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setPlaybackState(data);
    } catch (e) {
      if (isScopeError(e.message)) setScopeWarning(true);
      setPlaybackError(e.message);
      setPlaybackState(null);
    }
    setPlaybackLoading(false);
  };

  // Pasek postepu plynie miedzy odpytaniami z lokalnego zegara — ZERO zapytan do Spotify.
  // Dzieki temu suwak rusza sie co sekunde, a ruch po sieci jest raz na kilkanascie sekund.
  useEffect(() => {
    if (!playbackState?.active) {
      setDisplayProgressMs(null);
      return;
    }
    const base = playbackState.progress_ms ?? 0;
    setDisplayProgressMs(base);
    if (!playbackState.is_playing) return;
    const startedAt = Date.now();
    const duration = playbackState.track?.duration_ms ?? Number.MAX_SAFE_INTEGER;
    const id = setInterval(() => {
      setDisplayProgressMs(Math.min(base + (Date.now() - startedAt), duration));
    }, 1000);
    return () => clearInterval(id);
  }, [playbackState]);

  const controlPlayback = async (action) => {
    const endpoints = {
      play: { method: "PUT", path: "/spotify/playback/play" },
      pause: { method: "PUT", path: "/spotify/playback/pause" },
      next: { method: "POST", path: "/spotify/playback/next" },
      previous: { method: "POST", path: "/spotify/playback/previous" },
    };
    const { method, path } = endpoints[action];
    try {
      const res = await fetch(`${API}${path}?access_token=${auth.accessToken}`, { method });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setTimeout(fetchPlaybackState, 500);
    } catch (e) {
      handleApiError(e, "Sterowanie odtwarzaniem");
    }
  };

  const setPlaybackVolume = async (volumePercent) => {
    try {
      const res = await fetch(`${API}/spotify/playback/volume?access_token=${auth.accessToken}&volume_percent=${volumePercent}`, { method: "PUT" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
    } catch (e) {
      handleApiError(e, "Zmiana głośności");
    }
  };

  // --- Przewijanie, shuffle, repeat ---
  const seekPlayback = async (positionMs) => {
    try {
      const res = await fetch(`${API}/spotify/playback/seek?access_token=${auth.accessToken}&position_ms=${Math.round(positionMs)}`, { method: "PUT" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setPlaybackState(prev => (prev ? { ...prev, progress_ms: Math.round(positionMs) } : prev));
    } catch (e) {
      handleApiError(e, "Przewijanie");
    }
  };

  const toggleShuffle = async () => {
    const next = !playbackState?.shuffle_state;
    try {
      const res = await fetch(`${API}/spotify/playback/shuffle?access_token=${auth.accessToken}&state=${next}`, { method: "PUT" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setPlaybackState(prev => (prev ? { ...prev, shuffle_state: next } : prev));
      toast.success(next ? "Losowa kolejność włączona" : "Losowa kolejność wyłączona");
    } catch (e) {
      handleApiError(e, "Tryb losowy");
    }
  };

  // Spotify ma trzy stany powtarzania — klikanie przechodzi po nich w kolko
  const REPEAT_CYCLE = { off: "context", context: "track", track: "off" };
  const REPEAT_LABELS = { off: "Powtarzanie wyłączone", context: "Powtarzaj całość", track: "Powtarzaj utwór" };

  const cycleRepeat = async () => {
    const next = REPEAT_CYCLE[playbackState?.repeat_state] ?? "context";
    try {
      const res = await fetch(`${API}/spotify/playback/repeat?access_token=${auth.accessToken}&state=${next}`, { method: "PUT" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setPlaybackState(prev => (prev ? { ...prev, repeat_state: next } : prev));
      toast.success(REPEAT_LABELS[next]);
    } catch (e) {
      handleApiError(e, "Powtarzanie");
    }
  };

  // --- Urzadzenia Spotify Connect ---
  const [devices, setDevices] = useState(null);
  const [loadingDevices, setLoadingDevices] = useState(false);

  const loadDevices = async () => {
    setLoadingDevices(true);
    try {
      const res = await fetch(`${API}/spotify/devices?access_token=${auth.accessToken}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setDevices(data.devices || []);
    } catch (e) {
      handleApiError(e, "Lista urządzeń");
      setDevices(null);
    }
    setLoadingDevices(false);
  };

  const transferPlayback = async (deviceId, deviceName, play = true) => {
    try {
      const res = await fetch(`${API}/spotify/playback/transfer?access_token=${auth.accessToken}&device_id=${deviceId}&play=${play}`, { method: "PUT" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      toast.success(`Przeniesiono odtwarzanie na: ${deviceName}`);
      setTimeout(() => { fetchPlaybackState(); loadDevices(); }, 800);
    } catch (e) {
      handleApiError(e, "Przeniesienie odtwarzania");
    }
  };

  // --- Ostatnio odtwarzane ---
  const [recentlyPlayed, setRecentlyPlayed] = useState(null);
  const [loadingRecent, setLoadingRecent] = useState(false);

  const loadRecentlyPlayed = async () => {
    setLoadingRecent(true);
    try {
      const res = await fetch(`${API}/spotify/recently-played?access_token=${auth.accessToken}&limit=50`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setRecentlyPlayed(data.tracks || []);
    } catch (e) {
      handleApiError(e, "Historia odtwarzania");
      setRecentlyPlayed(null);
    }
    setLoadingRecent(false);
  };

  // --- Analiza audio (iTunes + librosa) ---
  // Spotify blokuje preview_url od 11/2024, wiec probki pochodza z iTunes Search API,
  // a cechy sygnalu liczy backend lokalnie (librosa). Tu tylko odczyt i sterowanie.
  const [audioCoverage, setAudioCoverage] = useState(null);
  const [dspStatus, setDspStatus] = useState(null);
  const [dspStarting, setDspStarting] = useState(false);
  const [dspSource, setDspSource] = useState("all_uncached");
  const [dspTestQuery, setDspTestQuery] = useState({ track: "", artist: "" });
  const [dspTestResult, setDspTestResult] = useState(null);
  const [dspTesting, setDspTesting] = useState(false);

  const fetchAudioCoverage = async () => {
    try {
      const res = await fetch(`${API}/audio/coverage`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setAudioCoverage(data);
    } catch (e) {
      console.warn("Pokrycie analizy DSP niedostępne:", e.message);
    }
  };

  const fetchDspStatus = async () => {
    try {
      const res = await fetch(`${API}/audio/analyze-batch/status?access_token=${auth.accessToken}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setDspStatus(data);
      return data;
    } catch (e) {
      console.warn("Status analizy DSP niedostępny:", e.message);
      return null;
    }
  };

  const startDspBatch = async () => {
    setDspStarting(true);
    try {
      const res = await fetch(`${API}/audio/analyze-batch?access_token=${auth.accessToken}&source=${dspSource}&limit=25000`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      toast.success(`Analiza DSP wystartowała — ${data.tracks_queued} utworów w kolejce`);
      fetchDspStatus(); // dalszy postep dociaga efekt sledzacy zadania w tle
    } catch (e) {
      toast.error(`Nie udało się wystartować analizy: ${e.message}`);
    }
    setDspStarting(false);
  };

  const testDspFeatures = async () => {
    if (!dspTestQuery.track.trim() || !dspTestQuery.artist.trim()) {
      toast.error("Podaj tytuł utworu i wykonawcę");
      return;
    }
    setDspTesting(true);
    setDspTestResult(null);
    try {
      const params = new URLSearchParams({ track_name: dspTestQuery.track, artist_name: dspTestQuery.artist });
      const res = await fetch(`${API}/audio/dsp-features?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setDspTestResult(data);
    } catch (e) {
      toast.error(`Analiza utworu nie powiodła się: ${e.message}`);
    }
    setDspTesting(false);
  };

  // --- Odtwarzanie 30s probki z iTunes (dziala bez Premium, bez Spotify) ---
  const [previewTrackId, setPreviewTrackId] = useState(null);
  const [loadingPreviewId, setLoadingPreviewId] = useState(null);
  const previewAudioRef = useRef(null);

  const toggleItunesPreview = async (track) => {
    const audio = previewAudioRef.current;
    if (!audio) return;
    if (previewTrackId === track.id) {
      audio.pause();
      setPreviewTrackId(null);
      return;
    }
    setLoadingPreviewId(track.id);
    try {
      const params = new URLSearchParams({
        track_name: track.name,
        artist_name: track.artists?.[0]?.name || "",
      });
      const res = await fetch(`${API}/audio/itunes-preview?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      if (!data.preview_url) throw new Error("iTunes nie ma fragmentu dla tego utworu");
      audio.src = data.preview_url;
      await audio.play();
      setPreviewTrackId(track.id);
    } catch (e) {
      toast.error(`Próbka audio: ${e.message}`);
      setPreviewTrackId(null);
    }
    setLoadingPreviewId(null);
  };

  // --- Obserwowanie artystow ---
  const [followedMap, setFollowedMap] = useState({});
  const [followingList, setFollowingList] = useState(null);
  const [loadingFollowing, setLoadingFollowing] = useState(false);

  const toggleFollow = async (artistId, artistName, currentlyFollowed) => {
    if (!artistId) {
      toast.error("Brak ID artysty (Spotify niedostepne przy generowaniu) — nie mozna zaobserwowac");
      return;
    }
    try {
      const res = await fetch(`${API}/spotify/follow?access_token=${auth.accessToken}&artist_ids=${artistId}`, {
        method: currentlyFollowed ? "DELETE" : "PUT",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setFollowedMap(prev => ({ ...prev, [artistId]: !currentlyFollowed }));
      if (currentlyFollowed) {
        setFollowingList(prev => prev?.filter(a => a.id !== artistId) ?? prev);
        toast.success(`Przestałeś obserwować ${artistName}`);
      } else {
        toast.success(`Obserwujesz ${artistName}`);
      }
    } catch (e) {
      handleApiError(e, "Obserwowanie artysty");
    }
  };

  // --- Biblioteka: zapisz/usun utwor (Liked Songs) ---
  const [savedTracksMap, setSavedTracksMap] = useState({});

  const toggleSaveTrack = async (trackId, trackName, currentlySaved) => {
    if (!trackId) return;
    try {
      const res = await fetch(`${API}/spotify/library/tracks?access_token=${auth.accessToken}&track_ids=${trackId}`, {
        method: currentlySaved ? "DELETE" : "PUT",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setSavedTracksMap(prev => ({ ...prev, [trackId]: !currentlySaved }));
      toast.success(currentlySaved ? `Usunięto "${trackName}" z biblioteki` : `Zapisano "${trackName}" do biblioteki`);
    } catch (e) {
      handleApiError(e, "Zapis utworu do biblioteki");
    }
  };

  // --- Nowosci plytowe ---
  const [newReleases, setNewReleases] = useState(null);
  const [loadingNewReleases, setLoadingNewReleases] = useState(false);
  const [newReleasesBlocked, setNewReleasesBlocked] = useState(false);

  const loadNewReleases = async () => {
    setLoadingNewReleases(true);
    try {
      const res = await fetch(`${API}/spotify/new-releases?access_token=${auth.accessToken}&limit=20`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setNewReleases(data.albums || []);
      syncSavedAlbumsState();
      setNewReleasesBlocked(false);
    } catch (e) {
      // Zweryfikowane 2026-08-23: /browse/new-releases zwraca 403 dla tej aplikacji, mimo ze
      // nie wymaga zadnego scope — to ta sama fala ograniczen co audio-features (11/2024),
      // a nie problem z tokenem uzytkownika. Mowimy to wprost, zamiast generycznego bledu.
      if (/403/.test(e.message)) {
        setNewReleasesBlocked(true);
      } else {
        handleApiError(e, "Nowości płytowe");
      }
      setNewReleases(null);
    }
    setLoadingNewReleases(false);
  };

  // --- Szczegoly artysty (modal) ---
  const [artistDetail, setArtistDetail] = useState(null);
  const [loadingArtistDetail, setLoadingArtistDetail] = useState(false);

  const openArtistDetail = async (artistId, artistName) => {
    if (!artistId) return;
    setArtistDetail({ id: artistId, name: artistName, loading: true });
    setLoadingArtistDetail(true);
    try {
      const [detailRes, albumsRes, topTracksRes] = await Promise.all([
        fetch(`${API}/spotify/artist/${artistId}?access_token=${auth.accessToken}`),
        fetch(`${API}/spotify/artist/${artistId}/albums?access_token=${auth.accessToken}&limit=10`),
        fetch(`${API}/spotify/artist/${artistId}/top-tracks?access_token=${auth.accessToken}`),
      ]);
      const [detail, albumsData, topTracksData] = await Promise.all([detailRes.json(), albumsRes.json(), topTracksRes.json()]);
      if (!detailRes.ok) throw new Error(detail.detail || "Nie udało się pobrać danych artysty");
      setArtistDetail({
        ...detail,
        albums: albumsData.albums || [],
        topTracks: topTracksData.tracks || [],
        loading: false,
      });
    } catch (e) {
      toast.error(`Szczegóły artysty: ${e.message}`);
      setArtistDetail(null);
    }
    setLoadingArtistDetail(false);
  };

  const loadFollowing = async () => {
    setLoadingFollowing(true);
    try {
      const res = await fetch(`${API}/spotify/following?access_token=${auth.accessToken}&limit=50`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setFollowingList(data.artists || []);
      setFollowedMap(prev => {
        const next = { ...prev };
        (data.artists || []).forEach(a => { next[a.id] = true; });
        return next;
      });
    } catch (e) {
      handleApiError(e, "Lista obserwowanych");
      setFollowingList(null);
    }
    setLoadingFollowing(false);
  };

  // --- Realny stan "zapisane / obserwowane" zamiast zgadywania z klikniec ---
  // Bez tego serduszka i przyciski obserwowania startowaly zawsze jako "puste", nawet dla
  // utworow ktore user ma w bibliotece od lat — stan pojawial sie dopiero po kliknieciu.
  // Spotify zwraca 403 na WSZYSTKICH koncowkach `/contains` (zweryfikowane 2026-08-23 —
  // ograniczenie aplikacji, nie brak scope; pelne listy `/me/tracks`, `/me/albums`,
  // `/me/following` dzialaja normalnie). Dlatego stan bierzemy z list, nie z pytania
  // "czy zapisane?" — jedno zapytanie zamiast dziesiatek, i faktycznie dziala.
  const syncSavedTracksState = async () => {
    try {
      // Z lokalnej kopii biblioteki — zero zapytan do Spotify
      const res = await fetch(`${API}/library/saved-track-ids?access_token=${auth.accessToken}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      const map = {};
      (data.track_ids || []).forEach(id => { map[id] = true; });
      setSavedTracksMap(prev => ({ ...map, ...prev }));
    } catch (e) {
      if (isScopeError(e.message)) setScopeWarning(true);
      console.warn("Nie udało się wczytać listy polubionych utworów:", e.message);
    }
  };

  const syncFollowedState = async () => {
    try {
      const res = await fetch(`${API}/spotify/following/all?access_token=${auth.accessToken}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      const map = {};
      (data.artists || []).forEach(a => { map[a.id] = true; });
      setFollowedMap(prev => ({ ...map, ...prev }));
      setFollowingList(data.artists || []);
    } catch (e) {
      if (isScopeError(e.message)) setScopeWarning(true);
      console.warn("Nie udało się wczytać listy obserwowanych artystów:", e.message);
    }
  };

  // --- Albumy z nowosci: zapisz/usun + realny stan ---
  const [savedAlbumsMap, setSavedAlbumsMap] = useState({});

  const syncSavedAlbumsState = async () => {
    try {
      const res = await fetch(`${API}/spotify/library/album-ids?access_token=${auth.accessToken}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      const map = {};
      (data.album_ids || []).forEach(id => { map[id] = true; });
      setSavedAlbumsMap(prev => ({ ...map, ...prev }));
    } catch (e) {
      if (isScopeError(e.message)) setScopeWarning(true);
      console.warn("Nie udało się wczytać listy zapisanych albumów:", e.message);
    }
  };

  const toggleSaveAlbum = async (albumId, albumName, currentlySaved) => {
    if (!albumId) return;
    try {
      const res = await fetch(`${API}/spotify/library/albums?access_token=${auth.accessToken}&album_ids=${albumId}`, {
        method: currentlySaved ? "DELETE" : "PUT",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setSavedAlbumsMap(prev => ({ ...prev, [albumId]: !currentlySaved }));
      toast.success(currentlySaved ? `Usunięto album "${albumName}"` : `Zapisano album "${albumName}"`);
    } catch (e) {
      handleApiError(e, "Zapis albumu");
    }
  };

  // --- Okladka playlisty (ugc-image-upload) ---
  // Jeden ukryty <input type="file"> na cala liste — otwierany programowo przez przycisk
  // w wierszu playlisty, bo wiersz jest linkiem i <label> wywolalby nawigacje.
  const [uploadingCover, setUploadingCover] = useState(null);
  const [coverTarget, setCoverTarget] = useState(null);
  const coverInputRef = useRef(null);

  const uploadPlaylistCover = async (playlistId, playlistName, file) => {
    if (!file) return;
    // Spotify przyjmuje wylacznie JPEG, max 256 KB PO zakodowaniu base64
    if (file.type !== "image/jpeg") {
      toast.error("Spotify przyjmuje tylko pliki JPEG jako okładkę playlisty");
      return;
    }
    setUploadingCover(playlistId);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",")[1]);
        reader.onerror = () => reject(new Error("Nie udało się odczytać pliku"));
        reader.readAsDataURL(file);
      });
      if (base64.length > 256 * 1024) {
        throw new Error(`Obrazek po zakodowaniu ma ${Math.round(base64.length / 1024)} KB — Spotify dopuszcza max 256 KB`);
      }
      const res = await fetch(`${API}/spotify/playlist/${playlistId}/image?access_token=${auth.accessToken}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_base64: base64 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      toast.success(`Okładka playlisty "${playlistName}" zaktualizowana (Spotify potrzebuje chwili na odświeżenie)`);
    } catch (e) {
      handleApiError(e, "Zmiana okładki playlisty");
    }
    setUploadingCover(null);
  };

  // --- Web Playback SDK: odtwarzanie w tej karcie przegladarki ---
  // Wymaga Spotify Premium i scope 'streaming' (jest w SPOTIFY_SCOPES). Uruchamiane
  // dopiero po jawnym kliknieciu — nie chcemy ladowac zewnetrznego SDK dla kazdego usera.
  const [webPlayerEnabled, setWebPlayerEnabled] = useState(false);
  const [webPlayerDeviceId, setWebPlayerDeviceId] = useState(null);
  const [webPlayerError, setWebPlayerError] = useState(null);
  const [webPlayerState, setWebPlayerState] = useState(null);
  const webPlayerRef = useRef(null);

  useEffect(() => {
    if (!webPlayerEnabled || !auth.accessToken) return;
    let cancelled = false;

    const initPlayer = () => {
      if (cancelled || webPlayerRef.current || !window.Spotify) return;
      const player = new window.Spotify.Player({
        name: "Panspoti (przeglądarka)",
        // Token czytany za kazdym razem ze localStorage — App.js odswieza go co 45 min
        getOAuthToken: (cb) => cb(localStorage.getItem("spotify_access_token") || auth.accessToken),
        volume: 0.5,
      });
      player.addListener("ready", ({ device_id }) => {
        setWebPlayerDeviceId(device_id);
        setWebPlayerError(null);
        toast.success("Odtwarzacz w przeglądarce gotowy — przenieś na niego odtwarzanie");
      });
      player.addListener("not_ready", () => setWebPlayerDeviceId(null));
      player.addListener("player_state_changed", (state) => setWebPlayerState(state));
      player.addListener("initialization_error", ({ message }) => setWebPlayerError(`Inicjalizacja SDK: ${message}`));
      player.addListener("authentication_error", ({ message }) => setWebPlayerError(`Autoryzacja: ${message} — wyloguj się i zaloguj ponownie`));
      player.addListener("account_error", ({ message }) => setWebPlayerError(`Konto: ${message}. Odtwarzanie w przeglądarce działa wyłącznie z kontem Spotify Premium.`));
      player.addListener("playback_error", ({ message }) => toast.error(`Odtwarzanie: ${message}`));
      player.connect();
      webPlayerRef.current = player;
    };

    if (window.Spotify) {
      initPlayer();
    } else {
      window.onSpotifyWebPlaybackSDKReady = initPlayer;
      if (!document.getElementById("spotify-web-playback-sdk")) {
        const script = document.createElement("script");
        script.id = "spotify-web-playback-sdk";
        script.src = "https://sdk.scdn.co/spotify-player.js";
        script.async = true;
        script.onerror = () => setWebPlayerError("Nie udało się załadować Spotify Web Playback SDK — sprawdź blokadę skryptów / adblock");
        document.body.appendChild(script);
      }
    }

    return () => { cancelled = true; };
  }, [webPlayerEnabled, auth.accessToken]);

  // Rozlaczenie odtwarzacza przy opuszczeniu dashboardu — inaczej urzadzenie zostaje
  // widoczne na koncie Spotify mimo zamknietej karty
  useEffect(() => () => {
    if (webPlayerRef.current) {
      webPlayerRef.current.disconnect();
      webPlayerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!auth.accessToken) {
      navigate("/");
      return;
    }
    fetchData();
    loadLastRecommendations();
    fetchSyncStatus();
    fetchLibraryStats();
    fetchAudioCoverage();
    fetchDspStatus();
  }, [auth.accessToken, timeRange]);

  // Po zaladowaniu list z cache sprawdzamy realny stan w Spotify (serca / obserwowanie)
  // Jedno pobranie na wejscie — stan dotyczy calego konta, nie konkretnej listy na ekranie
  useEffect(() => {
    if (!auth.accessToken) return;
    syncSavedTracksState();
    syncFollowedState();
  }, [auth.accessToken]);

  // Auto-odswiezanie danych LOKALNYCH (Mongo) — nie kosztuje ani jednego zapytania do Spotify,
  // wiec moze chodzic od samego wejscia. Pauzuje, gdy karta jest w tle.
  useEffect(() => {
    if (!autoRefresh || !auth.accessToken) return;
    const id = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      fetchLibraryStats();
      fetchAudioCoverage();
      fetchSyncStatus();
      fetchDspStatus();
    }, 60000);
    return () => clearInterval(id);
  }, [autoRefresh, auth.accessToken]);

  // Zadanie w tle (synchronizacja / analiza DSP) — status co 5 s, NIEZALEZNIE od tego, ktora
  // karta je uruchomila. Wczesniej postep sledzila wylacznie ta karta, ktora kliknela przycisk:
  // po odswiezeniu strony (albo w drugiej karcie) licznik po prostu stal (2026-08-23).
  // To odczyt z Mongo, wiec czestotliwosc nie ma nic wspolnego z limitami Spotify.
  const syncRunning = syncStatus?.progress?.status === "running";
  const dspRunning = dspStatus?.progress?.status === "running";

  useEffect(() => {
    if (!auth.accessToken || (!syncRunning && !dspRunning)) return;
    const id = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      if (syncRunning) fetchSyncStatus();
      if (dspRunning) {
        fetchDspStatus();
        fetchAudioCoverage();
      }
    }, 5000);
    return () => clearInterval(id);
  }, [auth.accessToken, syncRunning, dspRunning]);

  // Komunikaty o zakonczeniu — na przejsciu stanu, nie w petli odpytujacej
  const prevSyncStatusRef = useRef(null);
  useEffect(() => {
    const status = syncStatus?.progress?.status;
    const prev = prevSyncStatusRef.current;
    prevSyncStatusRef.current = status;
    if (prev !== "running" || status === "running") return;
    setSyncing(false);
    if (status === "done") {
      toast.success("Synchronizacja biblioteki zakończona");
      fetchLibraryStats();
      syncSavedTracksState();
    } else if (status === "error") {
      toast.error(`Błąd synchronizacji: ${syncStatus.progress.error}`);
    } else if (status === "interrupted") {
      toast.error("Synchronizacja została przerwana (restart serwera) — uruchom ją ponownie");
      fetchLibraryStats();
    }
  }, [syncStatus]);

  const prevDspStatusRef = useRef(null);
  useEffect(() => {
    const status = dspStatus?.progress?.status;
    const prev = prevDspStatusRef.current;
    prevDspStatusRef.current = status;
    if (prev !== "running" || status === "running") return;
    fetchAudioCoverage();
    if (status === "done") {
      toast.success(`Analiza DSP zakończona — zmierzono ${dspStatus.progress.analyzed ?? 0} utworów`);
    } else if (status === "error") {
      toast.error(`Analiza DSP przerwana: ${dspStatus.progress.error}`);
    } else if (status === "interrupted") {
      toast.error("Analiza DSP została przerwana (restart serwera) — uruchom ją ponownie");
    }
  }, [dspStatus]);

  // Auto-odswiezanie stanu odtwarzania — JEDYNE, ktore rusza Spotify. Startuje dopiero gdy
  // wiemy, ze cos gra (czyli po pierwszym recznym "Odswiez" albo po przeniesieniu odtwarzania)
  // i gasnie samo, gdy odtwarzanie sie konczy. Nigdy nie odpytuje karty w tle.
  const playbackActive = !!playbackState?.active;
  useEffect(() => {
    if (!autoRefresh || !auth.accessToken || !playbackActive) return;
    const id = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      fetchPlaybackState(true);
    }, 15000);
    return () => clearInterval(id);
  }, [autoRefresh, auth.accessToken, playbackActive]);

  const fetchSyncStatus = async () => {
    try {
      const res = await fetch(`${API}/library/sync/status?access_token=${auth.accessToken}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setSyncStatus(data);
      return data;
    } catch (e) {
      console.warn("Status synchronizacji niedostępny:", e.message);
      return null;
    }
  };

  const fetchLibraryStats = async () => {
    try {
      const res = await fetch(`${API}/library/stats?access_token=${auth.accessToken}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setLibraryStats(data);
      setLibraryStatsError(null);
    } catch (e) {
      // Wczesniej puste catch — brak statystyk wygladal identycznie jak "nigdy nie synchronizowano"
      console.warn("Statystyki biblioteki niedostępne:", e.message);
      setLibraryStatsError(e.message);
    }
  };

  const startLibrarySync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/library/sync?access_token=${auth.accessToken}`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Błąd startu synchronizacji");
      toast.success("Synchronizacja rozpoczęta — to może potrwać kilka minut");
      fetchSyncStatus(); // dalszy postep dociaga efekt sledzacy zadania w tle
    } catch (e) {
      toast.error(e.message);
      setSyncing(false);
    }
  };

  const [savedGenomes, setSavedGenomes] = useState([]);
  const [activeGenome, setActiveGenome] = useState(null);

  const [genomesError, setGenomesError] = useState(null);

  const loadLastRecommendations = async () => {
    try {
      // Load ALL saved genomes
      const dnaRes = await fetch(`${API}/profile/sonic-dna?access_token=${auth.accessToken}`);
      const dnaData = await dnaRes.json();
      if (!dnaRes.ok) throw new Error(dnaData.detail || `Genomy: HTTP ${dnaRes.status}`);
      const genomes = dnaData.genomes || [];
      setSavedGenomes(genomes);

      // Load last recommendation history
      const histRes = await fetch(`${API}/recommendations/history?access_token=${auth.accessToken}&limit=1`);
      const histData = await histRes.json();
      if (!histRes.ok) throw new Error(histData.detail || `Historia rekomendacji: HTTP ${histRes.status}`);

      if (histData.history?.length > 0) {
        const last = histData.history[0];
        // Find matching genome
        const matchingGenome = genomes.find(g => g.seed_label === last.seed_label);
        setActiveGenome(last.seed_label || null);
        
        setRecommendations({
          recommendations: last.recommendations || [],
          sonic_dna: matchingGenome?.sonic_dna || last.sonic_dna || null,
          taste_profile_summary: last.taste_profile || '',
          top_genres: last.top_genres || [],
          seed_label: last.seed_label || '',
        });
      } else if (genomes.length > 0) {
        setActiveGenome(genomes[0].seed_label);
      }

      // Load saved feedback (thumbs up/down)
      const fbRes = await fetch(`${API}/recommendations/feedback?access_token=${auth.accessToken}`);
      const fbData = await fbRes.json();
      if (!fbRes.ok) throw new Error(fbData.detail || `Feedback: HTTP ${fbRes.status}`);
      const fbMap = {};
      (fbData.feedback || []).forEach(f => { fbMap[f.artist_id] = f.action; });
      setFeedbackMap(fbMap);
      setGenomesError(null);
    } catch (e) {
      // Wczesniej puste catch — blad wygladal identycznie jak "nigdy nie generowano rekomendacji"
      console.error("Ładowanie zapisanych genomów:", e);
      setGenomesError(e.message);
      toast.error(`Nie udało się wczytać zapisanych genomów: ${e.message}`);
    }
  };

  const switchGenome = async (genome) => {
    setActiveGenome(genome.seed_label);
    // Load recommendations for this genome
    try {
      const histRes = await fetch(`${API}/recommendations/history?access_token=${auth.accessToken}&limit=10`);
      const histData = await histRes.json();
      if (!histRes.ok) throw new Error(histData.detail || `Historia rekomendacji: HTTP ${histRes.status}`);
      const match = histData.history?.find(h => h.seed_label === genome.seed_label);
      if (match) {
        setRecommendations({
          recommendations: match.recommendations || [],
          sonic_dna: genome.sonic_dna || match.sonic_dna || null,
          taste_profile_summary: match.taste_profile || '',
          top_genres: match.top_genres || [],
          seed_label: match.seed_label || '',
        });
      } else {
        setRecommendations({
          recommendations: [],
          sonic_dna: genome.sonic_dna,
          taste_profile_summary: '',
          top_genres: genome.top_genres || [],
          seed_label: genome.seed_label,
        });
      }
    } catch (e) {
      console.error("Przełączanie genomu:", e);
      toast.error(`Nie udało się wczytać rekomendacji dla tego genomu: ${e.message}`);
    }
  };

  const deleteGenome = async (seedLabel) => {
    try {
      const res = await fetch(`${API}/profile/sonic-dna?access_token=${auth.accessToken}&seed_label=${encodeURIComponent(seedLabel)}`, {method: 'DELETE'});
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setSavedGenomes(prev => prev.filter(g => g.seed_label !== seedLabel));
      if (activeGenome === seedLabel) setActiveGenome(null);
      toast.success('Genom usunięty');
    } catch (e) { toast.error(`Błąd usuwania genomu: ${e.message}`); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Czytamy z lokalnego cache (/library/sync), nie na zywo ze Spotify — dziala
      // nawet gdy Spotify jest niedostepne (429/5xx), pod warunkiem ze user juz
      // zsynchronizowal biblioteke chocby raz.
      const [artistsRes, tracksRes, playlistsRes] = await Promise.all([
        fetch(`${API}/library/top-artists?access_token=${auth.accessToken}&time_range=${timeRange}&limit=50`),
        fetch(`${API}/library/top-tracks?access_token=${auth.accessToken}&time_range=${timeRange}&limit=50`),
        fetch(`${API}/library/playlists?access_token=${auth.accessToken}`)
      ]);

      const [artistsData, tracksData, playlistsData] = await Promise.all([
        artistsRes.json(),
        tracksRes.json(),
        playlistsRes.json()
      ]);

      // fetch() nie rzuca bledu przy HTTP 4xx/5xx — bez tego sprawdzenia odpowiedz
      // bledu ({"detail": "..."}) byla po cichu traktowana jak {"tracks": []}
      if (!artistsRes.ok) throw new Error(artistsData.detail || `Top artyści: HTTP ${artistsRes.status}`);
      if (!tracksRes.ok) throw new Error(tracksData.detail || `Top utwory: HTTP ${tracksRes.status}`);
      if (!playlistsRes.ok) throw new Error(playlistsData.detail || `Playlisty: HTTP ${playlistsRes.status}`);

      setTopArtists(artistsData.artists || []);
      setTopTracks(tracksData.tracks || []);
      setPlaylists(playlistsData.playlists || []);
      // /library/playlists zwraca komplet za jednym razem (z cache) — nie trzeba doladowywac
      setPlaylistsTotal(playlistsData.total ?? (playlistsData.playlists || []).length);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error(error.message || "Błąd pobierania danych");
    }
    setLoading(false);
  };

  const [seedSource, setSeedSource] = useState("top");
  const [allUserPlaylists, setAllUserPlaylists] = useState([]);
  const [loadingAllPlaylists, setLoadingAllPlaylists] = useState(false);

  const [aiProvider, setAiProvider] = useState("claude_api");
  const [aiProviders, setAiProviders] = useState([]);

  // Load available AI providers (jawny status konfiguracji, bez zgadywania)
  useEffect(() => {
    fetch(`${API}/ai/providers`)
      .then(res => res.json())
      .then(data => setAiProviders(data.providers || []))
      .catch(() => setAiProviders([]));
  }, []);

  // Load ALL user's playlists for seed source dropdown
  useEffect(() => {
    if (!auth.accessToken) return;
    const loadAll = async () => {
      setLoadingAllPlaylists(true);
      let all = [];
      let offset = 0;
      while (true) {
        try {
          const res = await fetch(`${API}/spotify/playlists?access_token=${auth.accessToken}&limit=50&offset=${offset}`);
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
          const batch = data.playlists || [];
          all = [...all, ...batch];
          if (batch.length < 50) break;
          offset += 50;
        } catch (e) {
          // Wczesniej ciche `catch { break; }` — lista seedow po prostu bywala niepelna
          console.warn(`Lista playlist do wyboru seedu urwana na offset=${offset}:`, e.message);
          if (offset === 0) toast.error(`Nie udało się pobrać listy playlist do wyboru źródła: ${e.message}`);
          break;
        }
      }
      setAllUserPlaylists(all);
      setLoadingAllPlaylists(false);
    };
    loadAll();
  }, [auth.accessToken]);

  const generateRecommendations = async () => {
    setGeneratingRecs(true);
    try {
      const response = await fetch(`${API}/recommendations/generate?access_token=${auth.accessToken}&limit=8&seed_source=${seedSource}&ai_provider=${aiProvider}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || `Blad HTTP ${response.status}`);
      setRecommendations(data);
      setActiveGenome(data.seed_label || seedSource);
      
      const providerLabel = aiProviders.find(p => p.id === data.ai_provider_used)?.label || data.ai_provider_used;
      toast.success(`Rekomendacje wygenerowane przez: ${providerLabel}`);
      // Refresh saved genomes list
      try {
        const dnaRes = await fetch(`${API}/profile/sonic-dna?access_token=${auth.accessToken}`);
        const dnaData = await dnaRes.json();
        if (!dnaRes.ok) throw new Error(dnaData.detail || `HTTP ${dnaRes.status}`);
        setSavedGenomes(dnaData.genomes || []);
      } catch (e) {
        console.warn("Odświeżenie listy genomów po generowaniu nie powiodło się:", e.message);
        toast.warning("Rekomendacje gotowe, ale lista genomów w panelu może być nieaktualna — odśwież stronę");
      }
    } catch (error) {
      console.error("Recommendations error:", error);
      toast.error(error.message || "Blad generowania rekomendacji");
    }
    setGeneratingRecs(false);
  };

  const [savingPlaylist, setSavingPlaylist] = useState(false);
  const [feedbackMap, setFeedbackMap] = useState({}); // {artistId: 'thumbs_up'|'thumbs_down'}

  const sendFeedback = async (rec, action) => {
    try {
      const params = new URLSearchParams({
        access_token: auth.accessToken,
        artist_id: rec.id,
        artist_name: rec.name,
        action,
        genome_match: rec.genome_match || '',
        dna_match: rec.dna_match || rec.reason || '',
        technical_traits: (rec.technical_traits || rec.musical_traits || []).join(','),
      });
      const res = await fetch(`${API}/recommendations/feedback?${params}`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      setFeedbackMap(prev => ({ ...prev, [rec.id]: action }));
      toast.success(action === 'thumbs_up' ? `👍 ${rec.name} — więcej takich!` : `👎 ${rec.name} — mniej takich`);
    } catch (e) {
      toast.error(`Błąd zapisywania feedbacku: ${e.message}`);
    }
  };

  const loadMorePlaylists = async () => {
    setLoadingMore('playlists');
    try {
      const newOffset = playlists.length;
      const res = await fetch(`${API}/spotify/playlists?access_token=${auth.accessToken}&limit=50&offset=${newOffset}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Blad HTTP ${res.status}`);
      const newPlaylists = data.playlists || [];
      if (newPlaylists.length > 0) {
        setPlaylists(prev => [...prev, ...newPlaylists]);
      }
      if (newPlaylists.length < 50) {
        setPlaylistsTotal(playlists.length + newPlaylists.length);
      }
    } catch (e) {
      console.error("Doładowanie playlist:", e);
      toast.error(`Nie udało się doładować playlist: ${e.message}`);
    }
    setLoadingMore(null);
  };

  const saveToPlaylist = async () => {
    if (!recommendations?.recommendations?.length) return;
    setSavingPlaylist(true);
    try {
      const artistIds = recommendations.recommendations.map(r => r.id).filter(Boolean).join(",");
      const response = await fetch(`${API}/recommendations/save-playlist?access_token=${auth.accessToken}&artist_ids=${artistIds}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.playlist_url) {
        toast.success(`Playlista utworzona! ${data.tracks_added} utworów dodanych`);
        window.open(data.playlist_url, '_blank');
      } else {
        throw new Error(data.detail || 'Błąd tworzenia playlisty');
      }
    } catch (error) {
      console.error("Playlist error:", error);
      toast.error("Nie udało się utworzyć playlisty");
    }
    setSavingPlaylist(false);
  };

  const handleLogout = () => {
    auth.logout();
    navigate("/");
  };

  const timeRangeLabels = {
    short_term: "Ostatni miesiąc",
    medium_term: "Ostatnie 6 miesięcy",
    long_term: "Wszystko"
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]" data-testid="loading-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#FF3366] border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-[#A1A1AA]">Analizuję Twój profil muzyczny...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="dashboard-page">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF3366] to-[#FF6B6B] flex items-center justify-center">
              <Waveform size={20} weight="bold" className="text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Pandora <span className="gradient-text">Vibes</span>
            </span>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 text-sm text-[#A1A1AA]">
              <Avatar className="w-8 h-8">
                <AvatarImage src={auth.user?.profile_image} alt={auth.user?.display_name} />
                <AvatarFallback className="bg-[#1A1A1A]">
                  <User size={16} />
                </AvatarFallback>
              </Avatar>
              <span>{auth.user?.display_name}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/endpoints")}
              className="text-[#A1A1AA] hover:text-white hover:bg-white/10 text-xs"
              data-testid="api-status-link"
            >
              API
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-[#A1A1AA] hover:text-white hover:bg-white/10"
              data-testid="logout-btn"
            >
              <SignOut size={18} />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Jeden odtwarzacz probek iTunes na cala strone */}
        <audio
          ref={previewAudioRef}
          onEnded={() => setPreviewTrackId(null)}
          onError={() => setPreviewTrackId(null)}
          className="hidden"
        />

        {/* Wspolny, ukryty selektor pliku dla okladek playlist */}
        <input
          ref={coverInputRef}
          type="file"
          accept="image/jpeg"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (coverTarget && file) uploadPlaylistCover(coverTarget.id, coverTarget.name, file);
          }}
        />
        {/* Time Range Selector */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="overline mb-1">Twoje DNA muzyczne</p>
            <h1 className="section-title text-2xl sm:text-3xl text-white">
              Analiza profilu
            </h1>
          </div>
          <Tabs value={timeRange} onValueChange={setTimeRange} className="w-full sm:w-auto">
            <TabsList className="bg-[#121212] border border-[#27272A]">
              <TabsTrigger value="short_term" className="data-[state=active]:bg-[#FF3366] data-[state=active]:text-white text-xs sm:text-sm">
                Miesiąc
              </TabsTrigger>
              <TabsTrigger value="medium_term" className="data-[state=active]:bg-[#FF3366] data-[state=active]:text-white text-xs sm:text-sm">
                6 miesięcy
              </TabsTrigger>
              <TabsTrigger value="long_term" className="data-[state=active]:bg-[#FF3366] data-[state=active]:text-white text-xs sm:text-sm">
                Wszystko
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Lokalna kopia biblioteki */}
        <Card className="bg-[#121212] border-[#27272A] mb-8">
          <CardContent className="pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-white mb-1">Biblioteka lokalna (poza Spotify)</h3>
              {syncing || syncStatus?.progress?.status === "running" ? (
                <p className="text-xs text-[#A1A1AA]">
                  Synchronizuję… polubione: {syncStatus?.progress?.liked_tracks ?? 0}, playlisty: {syncStatus?.progress?.playlists ?? 0}
                  {syncStatus?.progress?.playlists_total_to_sync
                    ? ` (zawartość: ${syncStatus.progress.playlist_tracks_synced}/${syncStatus.progress.playlists_total_to_sync})`
                    : ""}
                </p>
              ) : libraryStats ? (
                <>
                  <p className="text-xs text-[#A1A1AA]">
                    {libraryStats.liked_tracks} polubionych · {libraryStats.playlists} playlist · {libraryStats.cached_tracks_global} utworów w cache
                    {syncStatus?.progress?.finished_at && (
                      <> · ostatnia synchronizacja: {new Date(syncStatus.progress.finished_at).toLocaleString("pl-PL")}</>
                    )}
                  </p>
                  {libraryStats.playlists_content_blocked > 0 && (
                    <p className="text-xs text-[#FBBF24] mt-1">
                      ⚠ Spotify blokuje pobieranie zawartości playlist (403)
                      {libraryStats.playlists_content_blocked === libraryStats.playlists
                        ? ` — wszystkie ${libraryStats.playlists} playlist, także Twoje własne. Zsynchronizowane są tylko nazwy i okładki; playlisty nie nadają się więc jako źródło (seed) rekomendacji.`
                        : ` — ${libraryStats.playlists_content_blocked}/${libraryStats.playlists} playlist bez zsynchronizowanych utworów`}
                    </p>
                  )}
                  {syncStatus?.progress?.status === "interrupted" && (
                    <p className="text-xs text-[#FBBF24] mt-1">
                      ⚠ Poprzednia synchronizacja nie dokończyła się (przerwana{syncStatus.progress.started_at
                        ? ` — rozpoczęta ${new Date(syncStatus.progress.started_at).toLocaleString("pl-PL")}`
                        : ""}). Dane poniżej mogą być niepełne — uruchom synchronizację ponownie.
                    </p>
                  )}
                </>
              ) : libraryStatsError ? (
                <p className="text-xs text-[#F87171]">
                  Nie udało się pobrać statystyk biblioteki: {libraryStatsError}
                </p>
              ) : (
                <p className="text-xs text-[#A1A1AA]">Jeszcze nie synchronizowano</p>
              )}
            </div>
            <Button
              onClick={startLibrarySync}
              disabled={syncing || syncStatus?.progress?.status === "running"}
              className="bg-[#1A1A1A] hover:bg-[#27272A] text-white border border-[#27272A] shrink-0"
            >
              {syncing || syncStatus?.progress?.status === "running" ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Synchronizuję…</span>
                </div>
              ) : (
                "Synchronizuj teraz"
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Token wydany przed rozszerzeniem uprawnien — bez ponownego logowania czesc funkcji
            fizycznie nie moze zadzialac. Mowimy to wprost zamiast pokazywac same bledy. */}
        {scopeWarning && (
          <Card className="bg-[#1A1710] border-[#B45309] mb-8">
            <CardContent className="pt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-semibold text-[#FBBF24] mb-1">Token nie ma wszystkich uprawnień</h3>
                <p className="text-xs text-[#D6C9A8]">
                  Twoja sesja została wydana zanim aplikacja poprosiła o pełny zestaw uprawnień (sterowanie
                  odtwarzaniem, obserwowanie artystów, zapis do biblioteki, historia odsłuchań). Spotify odrzuca
                  te operacje z błędem „Insufficient client scope”. Wyloguj się i zaloguj ponownie — pojawi się
                  ekran zgody z nowymi uprawnieniami.
                </p>
              </div>
              <Button
                onClick={handleLogout}
                className="bg-[#FBBF24] hover:bg-[#F59E0B] text-black font-medium shrink-0"
              >
                Zaloguj ponownie
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Analiza audio — realnie zmierzone cechy sygnalu (iTunes + librosa), nie zgadywanie AI */}
        <Card className="bg-[#121212] border-[#27272A] mb-8">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Waveform size={16} className="text-[#FF3366]" />
                <h3 className="text-sm font-semibold text-white">Analiza audio (DSP)</h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { fetchAudioCoverage(); fetchDspStatus(); }}
                className="text-[#A1A1AA] hover:text-white hover:bg-white/5 text-xs"
              >
                Odśwież
              </Button>
            </div>

            {audioCoverage ? (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex-1 h-2 rounded-full bg-[#1A1A1A] overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#FF3366] to-[#FF6B6B]"
                      style={{ width: `${audioCoverage.coverage_pct}%` }}
                    />
                  </div>
                  <span className="text-xs text-white tabular-nums shrink-0">{audioCoverage.coverage_pct}%</span>
                </div>
                <p className="text-xs text-[#A1A1AA]">
                  {audioCoverage.analyzed_with_dsp} z {audioCoverage.total_tracks_cached} utworów ma zmierzone cechy ·{" "}
                  {audioCoverage.no_itunes_match} bez dopasowania w iTunes · {audioCoverage.not_yet_attempted} jeszcze niesprawdzonych
                </p>
              </>
            ) : (
              <p className="text-xs text-[#A1A1AA]">Statystyka pokrycia niedostępna.</p>
            )}

            {dspRunning && (
              <div className="mt-3 p-2 rounded-lg bg-[#1A1A1A] border border-[#27272A]">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4ADE80] animate-pulse" />
                  <span className="text-xs text-[#FBBF24]">
                    Analiza w toku: {dspStatus.progress.processed}/{dspStatus.progress.total}
                    {dspStatus.progress.total > 0 && ` (${Math.round(100 * dspStatus.progress.processed / dspStatus.progress.total)}%)`}
                  </span>
                </div>
                <div className="h-1 rounded-full bg-[#27272A] overflow-hidden">
                  <div
                    className="h-full bg-[#FBBF24] transition-all duration-500"
                    style={{ width: `${dspStatus.progress.total > 0 ? (100 * dspStatus.progress.processed / dspStatus.progress.total) : 0}%` }}
                  />
                </div>
                <p className="text-[10px] text-[#A1A1AA] mt-1.5">
                  zmierzone {dspStatus.progress.analyzed ?? 0} · bez próbki w iTunes {dspStatus.progress.no_preview ?? 0} · błędy {dspStatus.progress.failed ?? 0}
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 mt-4 flex-wrap">
              <select
                value={dspSource}
                onChange={(e) => setDspSource(e.target.value)}
                className="bg-[#1A1A1A] border border-[#27272A] text-white text-xs rounded-md px-2 py-1.5 outline-none focus:border-[#FF3366]"
              >
                <option value="all_uncached">Wszystkie jeszcze nieprzeanalizowane</option>
                <option value="liked">Polubione utwory</option>
                <option value="top_tracks_short_term">Top utwory — miesiąc</option>
                <option value="top_tracks_medium_term">Top utwory — 6 miesięcy</option>
                <option value="top_tracks_long_term">Top utwory — wszystko</option>
              </select>
              <Button
                onClick={startDspBatch}
                disabled={dspStarting || dspStatus?.progress?.status === "running"}
                size="sm"
                className="bg-[#1A1A1A] hover:bg-[#27272A] text-white border border-[#27272A] text-xs"
              >
                {dspStatus?.progress?.status === "running" ? "Analiza trwa…" : dspStarting ? "Startuję…" : "Uruchom analizę"}
              </Button>
            </div>

            {/* Test pojedynczego utworu — bez zapisu do cache */}
            <div className="mt-4 pt-4 border-t border-[#27272A]">
              <p className="text-xs text-[#A1A1AA] mb-2">Sprawdź pojedynczy utwór (bez zapisu do bazy):</p>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  value={dspTestQuery.track}
                  onChange={(e) => setDspTestQuery(q => ({ ...q, track: e.target.value }))}
                  placeholder="Tytuł utworu"
                  className="bg-[#1A1A1A] border border-[#27272A] text-white text-xs rounded-md px-2 py-1.5 outline-none focus:border-[#FF3366] flex-1 min-w-[140px]"
                />
                <input
                  value={dspTestQuery.artist}
                  onChange={(e) => setDspTestQuery(q => ({ ...q, artist: e.target.value }))}
                  placeholder="Wykonawca"
                  className="bg-[#1A1A1A] border border-[#27272A] text-white text-xs rounded-md px-2 py-1.5 outline-none focus:border-[#FF3366] flex-1 min-w-[140px]"
                />
                <Button
                  onClick={testDspFeatures}
                  disabled={dspTesting}
                  size="sm"
                  variant="ghost"
                  className="text-[#FF3366] hover:text-white hover:bg-[#FF3366]/10 text-xs"
                >
                  {dspTesting ? "Analizuję…" : "Zbadaj"}
                </Button>
              </div>
              {dspTestResult && (
                <div className="mt-3 text-xs text-[#A1A1AA] space-y-1">
                  <p className="text-white">
                    {dspTestResult.match?.itunes_track_name} — {dspTestResult.match?.itunes_artist_name}
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 tabular-nums">
                    <span>tempo: <span className="text-white">{dspTestResult.features?.tempo_bpm ?? "–"} BPM</span></span>
                    <span>energia (RMS): <span className="text-white">{dspTestResult.features?.energy_rms}</span></span>
                    <span>jasność: <span className="text-white">{dspTestResult.features?.spectral_centroid_hz} Hz</span></span>
                    <span>rolloff: <span className="text-white">{dspTestResult.features?.spectral_rolloff_hz} Hz</span></span>
                    <span>ZCR: <span className="text-white">{dspTestResult.features?.zero_crossing_rate}</span></span>
                  </div>
                  {dspTestResult.match?.preview_url && (
                    <audio controls src={dspTestResult.match.preview_url} className="w-full mt-2 h-8" />
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Zdalne sterowanie odtwarzaniem — dziala na juz aktywnym urzadzeniu (telefon/appka), bez Web Playback SDK */}
        <Card className="bg-[#121212] border-[#27272A] mb-8">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-white">Teraz gra</h3>
                {autoRefresh && playbackActive && (
                  <span className="text-[10px] text-[#4ADE80] flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#4ADE80] animate-pulse" />
                    na żywo
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setAutoRefresh(v => !v)}
                  className={`text-[10px] px-2 py-1 rounded border transition-colors ${
                    autoRefresh
                      ? "border-[#27272A] text-[#A1A1AA] hover:text-white"
                      : "border-[#27272A] text-[#71717A] hover:text-white"
                  }`}
                  title={autoRefresh
                    ? "Auto-odświeżanie włączone: dane lokalne co 60 s, stan odtwarzania co 15 s (tylko gdy coś gra i karta jest aktywna)"
                    : "Auto-odświeżanie wyłączone — odświeżasz tylko przyciskiem"}
                >
                  {autoRefresh ? "auto: wł." : "auto: wył."}
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => fetchPlaybackState()}
                  disabled={playbackLoading}
                  className="text-[#A1A1AA] hover:text-white hover:bg-white/5 text-xs"
                >
                  {playbackLoading ? "Sprawdzam…" : "Odśwież"}
                </Button>
              </div>
            </div>

            {playbackError && (
              <p className="text-xs text-[#F87171] mb-2">{playbackError}</p>
            )}

            {!playbackState && !playbackError && !playbackLoading && (
              <p className="text-xs text-[#A1A1AA]">Kliknij "Odśwież" żeby sprawdzić co teraz gra na Twoim koncie.</p>
            )}

            {playbackState && !playbackState.active && (
              <p className="text-xs text-[#A1A1AA]">Brak aktywnego urządzenia — otwórz Spotify na telefonie lub komputerze.</p>
            )}

            {playbackState?.active && (
              <>
                <div className="flex items-center gap-4">
                  {playbackState.track?.album?.image && (
                    <img
                      src={playbackState.track.album.image}
                      alt={playbackState.track.album.name || ""}
                      className="w-12 h-12 rounded object-cover shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{playbackState.track?.name || "?"}</p>
                    <p className="text-xs text-[#A1A1AA] truncate">
                      {playbackState.track?.artists?.map(a => a.name).join(", ") || ""}
                      {playbackState.device?.name && ` · ${playbackState.device.name}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleShuffle}
                      className={`p-2 hover:bg-white/10 ${playbackState.shuffle_state ? "text-[#FF3366]" : "text-[#71717A]"}`}
                      title={playbackState.shuffle_state ? "Wyłącz losową kolejność" : "Włącz losową kolejność"}
                    >
                      <Shuffle size={16} weight="bold" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => controlPlayback("previous")} className="text-white hover:bg-white/10 p-2">
                      <SkipBack size={16} weight="fill" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => controlPlayback(playbackState.is_playing ? "pause" : "play")} className="text-white hover:bg-white/10 p-2">
                      {playbackState.is_playing ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => controlPlayback("next")} className="text-white hover:bg-white/10 p-2">
                      <SkipForward size={16} weight="fill" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={cycleRepeat}
                      className={`p-2 hover:bg-white/10 ${playbackState.repeat_state && playbackState.repeat_state !== "off" ? "text-[#FF3366]" : "text-[#71717A]"}`}
                      title={REPEAT_LABELS[playbackState.repeat_state] || "Powtarzanie"}
                    >
                      {playbackState.repeat_state === "track"
                        ? <RepeatOnce size={16} weight="bold" />
                        : <Repeat size={16} weight="bold" />}
                    </Button>
                  </div>
                  {playbackState.device?.volume_percent != null && (
                    <div className="flex items-center gap-2 shrink-0 w-24">
                      <SpeakerHigh size={14} className="text-[#A1A1AA]" />
                      <input
                        type="range"
                        min="0"
                        max="100"
                        defaultValue={playbackState.device.volume_percent}
                        onMouseUp={(e) => setPlaybackVolume(e.target.value)}
                        onTouchEnd={(e) => setPlaybackVolume(e.target.value)}
                        className="w-full accent-[#FF3366]"
                      />
                    </div>
                  )}
                </div>

                {/* Pasek postepu. Suwak plynie z lokalnego zegara (zero zapytan do Spotify),
                    a przewijanie wysyla jedno zapytanie dopiero po puszczeniu. */}
                {playbackState.track?.duration_ms > 0 && (
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-[10px] text-[#71717A] w-9 text-right tabular-nums">
                      {formatMs(seekDrag ?? displayProgressMs)}
                    </span>
                    <input
                      type="range"
                      min="0"
                      max={playbackState.track.duration_ms}
                      step="1000"
                      value={Math.min(seekDrag ?? displayProgressMs ?? 0, playbackState.track.duration_ms)}
                      onChange={(e) => setSeekDrag(Number(e.target.value))}
                      onMouseUp={(e) => { seekPlayback(e.target.value); setSeekDrag(null); }}
                      onTouchEnd={(e) => { seekPlayback(e.target.value); setSeekDrag(null); }}
                      className="flex-1 accent-[#FF3366]"
                    />
                    <span className="text-[10px] text-[#71717A] w-9 tabular-nums">
                      {formatMs(playbackState.track.duration_ms)}
                    </span>
                  </div>
                )}
              </>
            )}

            {/* Urzadzenia Spotify Connect + odtwarzacz w przegladarce */}
            <div className="mt-4 pt-4 border-t border-[#27272A]">
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <h4 className="text-xs font-semibold text-[#A1A1AA] uppercase tracking-wide">Urządzenia</h4>
                <div className="flex items-center gap-2">
                  {!webPlayerEnabled && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setWebPlayerEnabled(true)}
                      className="text-[#A1A1AA] hover:text-white hover:bg-white/5 text-xs"
                      title="Uruchamia Spotify Web Playback SDK w tej karcie (wymaga Premium)"
                    >
                      Graj w przeglądarce
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={loadDevices}
                    disabled={loadingDevices}
                    className="text-[#A1A1AA] hover:text-white hover:bg-white/5 text-xs"
                  >
                    {loadingDevices ? "Szukam…" : devices ? "Odśwież" : "Pokaż"}
                  </Button>
                </div>
              </div>

              {webPlayerEnabled && (
                <div className="mb-2 flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="border-[#27272A] text-[#A1A1AA] text-[10px]">
                    <Broadcast size={11} className="mr-1" />
                    {webPlayerDeviceId ? "Odtwarzacz w przeglądarce gotowy" : webPlayerError ? "Odtwarzacz niedostępny" : "Uruchamiam odtwarzacz…"}
                  </Badge>
                  {webPlayerDeviceId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => transferPlayback(webPlayerDeviceId, "Panspoti (przeglądarka)")}
                      className="text-[#FF3366] hover:text-white hover:bg-[#FF3366]/10 text-xs"
                    >
                      Przenieś odtwarzanie tutaj
                    </Button>
                  )}
                  {webPlayerState?.track_window?.current_track && (
                    <span className="text-[10px] text-[#A1A1AA] truncate">
                      w przeglądarce: {webPlayerState.track_window.current_track.name}
                    </span>
                  )}
                </div>
              )}

              {webPlayerError && (
                <p className="text-xs text-[#F87171] mb-2">{webPlayerError}</p>
              )}

              {devices === null ? (
                <p className="text-xs text-[#A1A1AA]">Kliknij "Pokaż" żeby zobaczyć urządzenia dostępne na Twoim koncie.</p>
              ) : devices.length === 0 ? (
                <p className="text-xs text-[#A1A1AA]">Brak dostępnych urządzeń — otwórz Spotify na telefonie lub komputerze.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {devices.map((device) => (
                    <button
                      key={device.id}
                      onClick={() => transferPlayback(device.id, device.name)}
                      disabled={device.is_active}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs transition-colors ${
                        device.is_active
                          ? "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366] cursor-default"
                          : "border-[#27272A] bg-[#1A1A1A] text-[#A1A1AA] hover:text-white hover:border-[#3f3f46]"
                      }`}
                      title={device.is_active ? "Aktywne urządzenie" : "Przenieś odtwarzanie na to urządzenie"}
                    >
                      <DeviceMobile size={13} />
                      <span>{device.name}</span>
                      <span className="text-[10px] text-[#71717A]">{device.type}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Ostatnio odtwarzane */}
        <Card className="bg-[#121212] border-[#27272A] mb-8">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ClockCounterClockwise size={16} className="text-[#FF3366]" />
                <h3 className="text-sm font-semibold text-white">Ostatnio odtwarzane</h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadRecentlyPlayed}
                disabled={loadingRecent}
                className="text-[#A1A1AA] hover:text-white hover:bg-white/5 text-xs"
              >
                {loadingRecent ? "Ładuję…" : recentlyPlayed ? "Odśwież" : "Pokaż"}
              </Button>
            </div>
            {recentlyPlayed === null ? (
              <p className="text-xs text-[#A1A1AA]">Kliknij "Pokaż" żeby zobaczyć ostatnie 50 odsłuchań (dane prosto ze Spotify, nie z lokalnego cache).</p>
            ) : recentlyPlayed.length === 0 ? (
              <p className="text-xs text-[#A1A1AA]">Spotify nie zwrócił historii odtwarzania.</p>
            ) : (
              <div className="max-h-[280px] overflow-y-auto pr-2 space-y-1">
                {recentlyPlayed.map((track, index) => (
                  <a
                    key={`${track.id}-${track.played_at || index}`}
                    href={track.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="track-row flex items-center gap-3 p-2 rounded-lg group"
                  >
                    <div className="w-8 h-8 rounded overflow-hidden flex-shrink-0">
                      {track.album?.image ? (
                        <img src={track.album.image} alt={track.album.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-[#1A1A1A] flex items-center justify-center">
                          <MusicNotes size={14} className="text-[#3f3f46]" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white truncate group-hover:text-[#FF3366] transition-colors">{track.name}</p>
                      <p className="text-[10px] text-[#A1A1AA] truncate">{track.artists?.map(a => a.name).join(", ")}</p>
                    </div>
                    {track.played_at && (
                      <span className="text-[10px] text-[#71717A] shrink-0">
                        {new Date(track.played_at).toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    )}
                  </a>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Obserwowani artysci */}
        <Card className="bg-[#121212] border-[#27272A] mb-8">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-white">Obserwowani artyści</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadFollowing}
                disabled={loadingFollowing}
                className="text-[#A1A1AA] hover:text-white hover:bg-white/5 text-xs"
              >
                {loadingFollowing ? "Ładuję…" : followingList ? "Odśwież" : "Pokaż"}
              </Button>
            </div>
            {followingList === null ? (
              <p className="text-xs text-[#A1A1AA]">Kliknij "Pokaż" żeby zobaczyć listę obserwowanych artystów.</p>
            ) : followingList.length === 0 ? (
              <p className="text-xs text-[#A1A1AA]">Nie obserwujesz jeszcze żadnego artysty.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {followingList.map((artist) => (
                  <div
                    key={artist.id}
                    className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full bg-[#1A1A1A] border border-[#27272A]"
                  >
                    <Avatar className="w-6 h-6">
                      <AvatarImage src={artist.image} alt={artist.name} />
                      <AvatarFallback className="bg-[#27272A] text-[10px]">{artist.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <span className="text-xs text-white">{artist.name}</span>
                    <button
                      onClick={() => toggleFollow(artist.id, artist.name, true)}
                      className="text-[#71717A] hover:text-[#F87171] transition-colors"
                      title="Przestań obserwować"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Nowosci plytowe */}
        <Card className="bg-[#121212] border-[#27272A] mb-8">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-white">Nowości płytowe</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadNewReleases}
                disabled={loadingNewReleases}
                className="text-[#A1A1AA] hover:text-white hover:bg-white/5 text-xs"
              >
                {loadingNewReleases ? "Ładuję…" : newReleases ? "Odśwież" : "Pokaż"}
              </Button>
            </div>
            {newReleasesBlocked ? (
              <p className="text-xs text-[#FBBF24]">
                ⚠ Spotify blokuje ten endpoint (403) dla tej aplikacji — <code>/browse/new-releases</code> nie wymaga
                żadnych uprawnień użytkownika, więc to ograniczenie po stronie Spotify (ta sama fala co audio-features
                z 11/2024), nie problem z Twoim tokenem. Odblokowanie wymagałoby Extended Quota Mode.
              </p>
            ) : newReleases === null ? (
              <p className="text-xs text-[#A1A1AA]">Kliknij "Pokaż" żeby zobaczyć najnowsze wydania polecane przez Spotify.</p>
            ) : newReleases.length === 0 ? (
              <p className="text-xs text-[#A1A1AA]">Brak danych o nowościach.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-3">
                {newReleases.map((album) => (
                  <div key={album.id} className="group relative">
                    <a
                      href={album.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`${album.name} — ${album.artists?.map(a => a.name).join(', ')}`}
                    >
                      <div className="aspect-square rounded-lg overflow-hidden bg-[#1A1A1A]">
                        {album.image ? (
                          <img src={album.image} alt={album.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <MusicNotes size={20} className="text-[#3f3f46]" />
                          </div>
                        )}
                      </div>
                      <p className="text-[10px] text-white truncate mt-1">{album.name}</p>
                      <p className="text-[10px] text-[#A1A1AA] truncate">{album.artists?.map(a => a.name).join(', ')}</p>
                    </a>
                    <button
                      onClick={() => toggleSaveAlbum(album.id, album.name, !!savedAlbumsMap[album.id])}
                      className={`absolute top-1 right-1 p-1 rounded-full bg-black/60 backdrop-blur transition-all ${
                        savedAlbumsMap[album.id]
                          ? 'text-[#FF3366]'
                          : 'text-white opacity-0 group-hover:opacity-100 hover:text-[#FF3366]'
                      }`}
                      title={savedAlbumsMap[album.id] ? "Usuń album z biblioteki" : "Zapisz album w bibliotece"}
                    >
                      <Heart size={12} weight={savedAlbumsMap[album.id] ? 'fill' : 'regular'} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Zapisane genomy nie wczytaly sie — jawny komunikat zamiast stanu "nic nie ma" */}
        {genomesError && (
          <Card className="bg-[#1A1010] border-[#7F1D1D] mb-8">
            <CardContent className="pt-6 flex items-center justify-between gap-4">
              <p className="text-xs text-[#FCA5A5]">
                Nie udało się wczytać zapisanych genomów i historii rekomendacji: {genomesError}.
                To, że poniżej nic nie widać, <strong>nie znaczy</strong> że nic nie wygenerowano.
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadLastRecommendations}
                className="text-[#FCA5A5] hover:text-white hover:bg-white/5 text-xs shrink-0"
              >
                Spróbuj ponownie
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Bento Grid */}
        <div className="dashboard-grid">
          {/* Top Artists Section - full width */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="col-span-12"
          >
            <Card className="bg-[#121212] border-[#27272A] h-full">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Heart size={20} className="text-[#FF3366]" />
                    <CardTitle className="text-lg text-white">Twoi Top Artyści</CardTitle>
                  </div>
                  <Badge variant="outline" className="border-[#27272A] text-[#A1A1AA]">
                    {timeRangeLabels[timeRange]}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                  {topArtists.slice(0, showAllArtists ? topArtists.length : 12).map((artist, index) => (
                    <TooltipProvider key={artist.id}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <motion.div
                            onClick={() => openArtistDetail(artist.id, artist.name)}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.05 }}
                            className="artist-card group cursor-pointer"
                            data-testid={`artist-card-${index}`}
                          >
                            {artist.image ? (
                              <img src={artist.image} alt={artist.name} />
                            ) : (
                              <div className="w-full h-full bg-[#1A1A1A] flex items-center justify-center">
                                <MusicNotes size={32} className="text-[#3f3f46]" />
                              </div>
                            )}
                            <div className="artist-overlay">
                              <span className="text-xs text-[#A1A1AA] mb-0.5">#{index + 1}</span>
                              <span className="text-sm font-medium text-white truncate">{artist.name}</span>
                            </div>
                            {/* Stan z pełnej listy obserwowanych (/following/all) — patrz syncFollowedState */}
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleFollow(artist.id, artist.name, !!followedMap[artist.id]); }}
                              className={`absolute top-1 right-1 z-10 p-1 rounded-full bg-black/60 backdrop-blur transition-all ${
                                followedMap[artist.id]
                                  ? 'text-[#FF3366]'
                                  : 'text-white opacity-0 group-hover:opacity-100 hover:text-[#FF3366]'
                              }`}
                              title={followedMap[artist.id] ? "Przestań obserwować" : "Obserwuj artystę"}
                            >
                              <User size={12} weight={followedMap[artist.id] ? 'fill' : 'regular'} />
                            </button>
                          </motion.div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="bg-[#1A1A1A] border-[#27272A]">
                          <p className="font-medium">{artist.name}</p>
                          {artist.genres?.[0] && (
                            <p className="text-xs text-[#A1A1AA] mt-1">{artist.genres[0]}</p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
                {topArtists.length > 12 && (
                  <Button
                    variant="ghost"
                    onClick={() => setShowAllArtists(!showAllArtists)}
                    className="w-full mt-4 text-[#A1A1AA] hover:text-white hover:bg-white/5"
                  >
                    {showAllArtists ? `Pokaż mniej` : `Pokaż wszystkich (${topArtists.length})`}
                  </Button>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Top Tracks Section - spans 6 columns */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="col-span-12 lg:col-span-6"
          >
            <Card className="bg-[#121212] border-[#27272A]">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                  <TrendUp size={20} className="text-[#FF3366]" />
                  <CardTitle className="text-lg text-white">Top Utwory</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className={`${showAllTracks ? '' : 'max-h-[400px] overflow-hidden'} pr-4`}>
                  <div className="space-y-2">
                    {topTracks.map((track, index) => (
                      <motion.a
                        key={track.id}
                        href={track.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className="track-row flex items-center gap-3 p-2 rounded-lg cursor-pointer group"
                        data-testid={`track-row-${index}`}
                      >
                        {/* Odtwarzanie 30s probki z iTunes — Spotify blokuje wlasne preview_url */}
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleItunesPreview(track); }}
                          className="w-6 shrink-0 flex items-center justify-center"
                          title={previewTrackId === track.id ? "Zatrzymaj próbkę" : "Odtwórz 30s próbkę (iTunes)"}
                        >
                          {loadingPreviewId === track.id ? (
                            <div className="w-3 h-3 border-2 border-[#FF3366] border-t-transparent rounded-full animate-spin" />
                          ) : previewTrackId === track.id ? (
                            <Pause size={14} className="text-[#FF3366]" weight="fill" />
                          ) : (
                            <>
                              <span className="text-sm text-[#A1A1AA] group-hover:hidden">{index + 1}</span>
                              <Play size={14} className="text-[#FF3366] hidden group-hover:block" weight="fill" />
                            </>
                          )}
                        </button>

                        <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0">
                          {track.album?.image ? (
                            <img src={track.album.image} alt={track.album.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-[#1A1A1A] flex items-center justify-center">
                              <MusicNotes size={16} className="text-[#3f3f46]" />
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate group-hover:text-[#FF3366] transition-colors">
                            {track.name}
                          </p>
                          <p className="text-xs text-[#A1A1AA] truncate">
                            {track.artists.map(a => a.name).join(", ")}
                          </p>
                        </div>
                        
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSaveTrack(track.id, track.name, !!savedTracksMap[track.id]); }}
                          className={`p-1 rounded transition-all ${
                            savedTracksMap[track.id]
                              ? 'text-[#FF3366]'
                              : 'text-[#71717A] opacity-0 group-hover:opacity-100 hover:text-[#FF3366]'
                          }`}
                          title={savedTracksMap[track.id] ? "Usuń z biblioteki" : "Zapisz do biblioteki"}
                        >
                          <Heart size={14} weight={savedTracksMap[track.id] ? 'fill' : 'regular'} />
                        </button>

                        <span className="text-xs text-[#71717A]">
                          {Math.floor(track.duration_ms / 60000)}:{String(Math.floor((track.duration_ms % 60000) / 1000)).padStart(2, '0')}
                        </span>
                      </motion.a>
                    ))}
                  </div>
                </div>
                {topTracks.length > 8 && (
                  <Button
                    variant="ghost"
                    onClick={() => setShowAllTracks(!showAllTracks)}
                    className="w-full mt-4 text-[#A1A1AA] hover:text-white hover:bg-white/5"
                  >
                    {showAllTracks ? `Pokaż mniej` : `Pokaż wszystkie (${topTracks.length})`}
                  </Button>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Playlists Section - spans 6 columns */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="col-span-12 lg:col-span-6"
          >
            <Card className="bg-[#121212] border-[#27272A]">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                  <Playlist size={20} className="text-[#FF3366]" />
                  <CardTitle className="text-lg text-white">Twoje Playlisty</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className={`${showAllPlaylists ? '' : 'max-h-[400px] overflow-hidden'} pr-4`}>
                  <div className="space-y-2">
                    {playlists.map((playlist, index) => (
                      <motion.a
                        key={playlist.id}
                        href={playlist.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className="track-row flex items-center gap-3 p-2 rounded-lg cursor-pointer group"
                        data-testid={`playlist-row-${index}`}
                      >
                        <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0">
                          {playlist.image ? (
                            <img src={playlist.image} alt={playlist.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-[#1A1A1A] flex items-center justify-center">
                              <Playlist size={20} className="text-[#3f3f46]" />
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate group-hover:text-[#FF3366] transition-colors">
                            {playlist.name}
                          </p>
                          <p className="text-xs text-[#A1A1AA]">
                            {/* Spotify nie podaje juz liczby utworow tej aplikacji — nie zmyslamy zera */}
                            {playlist.tracks_total == null ? "liczba utworów nieznana" : `${playlist.tracks_total} utworów`} • {playlist.owner}
                          </p>
                        </div>

                        {/* Zmiana okladki — tylko dla wlasnych playlist (Spotify odrzuci cudze).
                            Przycisk, nie <label> — wiersz jest linkiem <a>, wiec musimy zablokowac
                            domyslna nawigacje zanim otworzymy okno wyboru pliku. */}
                        {playlist.owner === auth.user?.display_name && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setCoverTarget({ id: playlist.id, name: playlist.name });
                              coverInputRef.current?.click();
                            }}
                            className="p-1 rounded text-[#71717A] opacity-0 group-hover:opacity-100 hover:text-[#FF3366] transition-all"
                            title="Zmień okładkę playlisty (JPEG, max 256 KB)"
                          >
                            {uploadingCover === playlist.id ? (
                              <div className="w-3.5 h-3.5 border-2 border-[#FF3366] border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <ImageSquare size={14} />
                            )}
                          </button>
                        )}

                        <CaretRight size={16} className="text-[#71717A] group-hover:text-white transition-colors" />
                      </motion.a>
                    ))}
                  </div>
                </div>
                {playlists.length > 8 && (
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="ghost"
                      onClick={() => setShowAllPlaylists(!showAllPlaylists)}
                      className="flex-1 text-[#A1A1AA] hover:text-white hover:bg-white/5"
                    >
                      {showAllPlaylists ? `Zwiń` : `Pokaż załadowane (${playlists.length})`}
                    </Button>
                    {(playlistsTotal === null || playlists.length < playlistsTotal) && showAllPlaylists && (
                      <Button
                        variant="ghost"
                        onClick={loadMorePlaylists}
                        disabled={loadingMore === 'playlists'}
                        className="text-[#FF3366] hover:text-white hover:bg-[#FF3366]/10"
                      >
                        {loadingMore === 'playlists' ? (
                          <div className="w-4 h-4 border-2 border-[#FF3366] border-t-transparent rounded-full animate-spin" />
                        ) : (
                          'Załaduj więcej'
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Saved Genomes Tabs */}
          {savedGenomes.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="col-span-12"
            >
              <Card className="bg-[#121212] border-[#27272A]">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-base">🧬</span>
                    <CardTitle className="text-lg text-white">Moje Genomy</CardTitle>
                    <Badge variant="outline" className="border-[#27272A] text-[#A1A1AA] ml-2">
                      {savedGenomes.length}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {savedGenomes.map(g => (
                      <div key={g.seed_label} className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => switchGenome(g)}
                          className={`text-sm ${activeGenome === g.seed_label
                            ? 'bg-[#FF3366]/20 text-[#FF3366] border border-[#FF3366]/30'
                            : 'text-[#A1A1AA] hover:text-white hover:bg-white/5 border border-[#27272A]'}`}
                        >
                          {g.seed_label || g.seed_source}
                        </Button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteGenome(g.seed_label); }}
                          className="text-[#71717A] hover:text-[#FF3366] text-xs px-1"
                          title="Usuń genom"
                        >×</button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* AI Recommendations Section - full width */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="col-span-12"
          >
            <Card className="bg-[#121212] border-[#27272A]">
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Sparkle size={20} className="text-[#FF3366]" />
                    <CardTitle className="text-lg text-white">
                      Odkryj Nowych Artystów
                    </CardTitle>
                    <Badge className="bg-[#FF3366]/20 text-[#FF3366] border-0 ml-2">
                      AI Pandora Style
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    {/* Seed Source Selector */}
                    <select
                      value={seedSource}
                      onChange={(e) => setSeedSource(e.target.value)}
                      className="bg-[#1A1A1A] border border-[#27272A] text-white text-sm rounded-md px-3 py-2 outline-none focus:border-[#FF3366] max-w-[250px]"
                    >
                      <option value="top">🎯 Mój profil (Top 50)</option>
                      <option value="liked">❤️ Ostatnie polubienia</option>
                      <optgroup label={loadingAllPlaylists ? "Ładowanie playlist..." : `Playlisty (${allUserPlaylists.length})`}>
                        {allUserPlaylists.map(pl => (
                          <option key={pl.id} value={pl.id}>📋 {pl.name} ({pl.tracks_total})</option>
                        ))}
                      </optgroup>
                    </select>
                    {/* AI Provider Selector — jawny wybor, zeby bylo wiadomo kto odpowiada */}
                    <select
                      value={aiProvider}
                      onChange={(e) => setAiProvider(e.target.value)}
                      className="bg-[#1A1A1A] border border-[#27272A] text-white text-sm rounded-md px-3 py-2 outline-none focus:border-[#FF3366] max-w-[220px]"
                      title="Ktory model AI wygeneruje rekomendacje"
                    >
                      {aiProviders.length === 0 && (
                        <option value="claude_api">Ladowanie providerow...</option>
                      )}
                      {aiProviders.map(p => (
                        <option key={p.id} value={p.id} disabled={!p.configured}>
                          {p.configured ? '✅' : '⛔'} {p.label}
                        </option>
                      ))}
                    </select>
                    <Button
                      onClick={generateRecommendations}
                      disabled={generatingRecs}
                      className="bg-[#FF3366] hover:bg-[#FF4477] text-white"
                      data-testid="generate-recommendations-btn"
                    >
                      {generatingRecs ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Analizuję...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <ArrowsClockwise size={18} />
                          <span>Generuj</span>
                        </div>
                      )}
                    </Button>
                    {recommendations?.recommendations?.length > 0 && (
                      <Button
                        onClick={saveToPlaylist}
                        disabled={savingPlaylist}
                        className="bg-[#1DB954] hover:bg-[#1ed760] text-white"
                      >
                        {savingPlaylist ? (
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span>Tworzę...</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <SpotifyLogo size={18} />
                            <span>Zapisz playlistę</span>
                          </div>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {recommendations ? (
                  <div className="space-y-6">
                    {recommendations.ai_provider_used && (
                      <Badge className="bg-[#27272A] text-[#A1A1AA] border-0 font-normal">
                        Wygenerowane przez: {aiProviders.find(p => p.id === recommendations.ai_provider_used)?.label || recommendations.ai_provider_used}
                      </Badge>
                    )}
                    {/* Sonic DNA Genomes */}
                    {recommendations.sonic_dna?.genomes?.length > 0 ? (
                      <div className="space-y-3">
                        <p className="overline">Twoje Sonic DNA</p>
                        {recommendations.sonic_dna.genomes.map((genome, i) => (
                          <div key={i} className="p-4 rounded-lg bg-[#1A1A1A] border border-[#27272A]">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-base">🧬</span>
                              <h4 className="font-semibold text-white text-sm">{genome.name}</h4>
                              {genome.artists_from_list?.length > 0 && (
                                <span className="text-[10px] text-[#A1A1AA] ml-auto">
                                  {genome.artists_from_list.join(' · ')}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-[#A1A1AA] leading-relaxed mb-2">
                              {genome.description}
                            </p>
                            {genome.connection && (
                              <p className="text-xs text-[#FF3366] italic mb-2">
                                → {genome.connection}
                              </p>
                            )}
                            {genome.traits?.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {genome.traits.map((trait, j) => (
                                  <span key={j} className="text-[10px] px-2 py-0.5 rounded-full bg-[#27272A] text-[#A1A1AA]">
                                    {trait}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                        {recommendations.sonic_dna.overall_summary && (
                          <p className="text-xs text-[#A1A1AA] italic px-1">
                            {recommendations.sonic_dna.overall_summary}
                          </p>
                        )}
                        {recommendations.top_genres?.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {recommendations.top_genres.map((genre, i) => (
                              <span key={i} className="genre-tag">{genre}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : recommendations.taste_profile_summary ? (
                      <div className="p-4 rounded-lg bg-[#1A1A1A] border border-[#27272A]">
                        <p className="overline mb-2">Twój Profil Muzyczny</p>
                        <p className="text-[#FAFAFA] text-sm leading-relaxed">
                          {recommendations.taste_profile_summary}
                        </p>
                      </div>
                    ) : null}

                    {/* Recommendations Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {recommendations.recommendations?.map((rec, index) => (
                        <motion.a
                          key={rec.id}
                          href={rec.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="recommendation-card group bg-[#1A1A1A] border border-[#27272A] rounded-xl p-4 hover:border-white/20 transition-all cursor-pointer"
                          data-testid={`recommendation-card-${index}`}
                        >
                          <div className="flex items-start gap-3 mb-3">
                            <Avatar className="w-14 h-14 rounded-lg shrink-0">
                              <AvatarImage src={rec.image} alt={rec.name} className="object-cover" />
                              <AvatarFallback className="bg-[#27272A] rounded-lg">
                                <MusicNotes size={24} className="text-[#3f3f46]" />
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-semibold text-white group-hover:text-[#FF3366] transition-colors">
                                  {rec.name}
                                </h4>
                                {rec.discovery_score && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FF3366]/20 text-[#FF3366]">
                                    Discovery {rec.discovery_score}/10
                                  </span>
                                )}
                              </div>
                              {rec.similar_to && (
                                <p className="text-xs text-[#A1A1AA] mt-0.5">
                                  Podobny do: {rec.similar_to}
                                </p>
                              )}
                              {rec.genome_match && (
                                <p className="text-[10px] text-[#FF3366] mt-1">🧬 {rec.genome_match}</p>
                              )}
                            </div>
                          </div>

                          <p className="text-xs text-[#A1A1AA] mb-3 leading-relaxed">
                            {rec.dna_match || rec.reason}
                          </p>

                          {(rec.technical_traits || rec.musical_traits)?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {(rec.technical_traits || rec.musical_traits).slice(0, 3).map((trait, i) => (
                                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-[#27272A] text-[#A1A1AA]">
                                  {trait}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          <div className="mt-3 pt-3 border-t border-[#27272A] flex items-center justify-between">
                            <div className="flex flex-wrap gap-1">
                              {rec.genres?.slice(0, 2).map((genre, i) => (
                                <span key={i} className="genre-tag text-[10px]">{genre}</span>
                              ))}
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); sendFeedback(rec, 'thumbs_up'); }}
                                className={`p-1.5 rounded-lg transition-all ${
                                  feedbackMap[rec.id] === 'thumbs_up'
                                    ? 'bg-green-500/20 text-green-400'
                                    : 'text-[#71717A] hover:text-green-400 hover:bg-green-500/10'
                                }`}
                                title="Więcej takich!"
                              >
                                <ThumbsUp size={16} weight={feedbackMap[rec.id] === 'thumbs_up' ? 'fill' : 'regular'} />
                              </button>
                              <button
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); sendFeedback(rec, 'thumbs_down'); }}
                                className={`p-1.5 rounded-lg transition-all ${
                                  feedbackMap[rec.id] === 'thumbs_down'
                                    ? 'bg-red-500/20 text-red-400'
                                    : 'text-[#71717A] hover:text-red-400 hover:bg-red-500/10'
                                }`}
                                title="Mniej takich"
                              >
                                <ThumbsDown size={16} weight={feedbackMap[rec.id] === 'thumbs_down' ? 'fill' : 'regular'} />
                              </button>
                              <button
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFollow(rec.id, rec.name, !!followedMap[rec.id]); }}
                                className={`p-1.5 rounded-lg transition-all ${
                                  followedMap[rec.id]
                                    ? 'bg-[#FF3366]/20 text-[#FF3366]'
                                    : 'text-[#71717A] hover:text-[#FF3366] hover:bg-[#FF3366]/10'
                                }`}
                                title={followedMap[rec.id] ? "Przestań obserwować" : "Obserwuj artystę"}
                              >
                                <Heart size={16} weight={followedMap[rec.id] ? 'fill' : 'regular'} />
                              </button>
                              <SpotifyLogo size={16} className="text-[#1DB954] opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
                            </div>
                          </div>
                        </motion.a>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-[#1A1A1A] flex items-center justify-center mx-auto mb-4">
                      <Sparkle size={32} className="text-[#FF3366]" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Odkryj swoich nowych ulubionych artystów
                    </h3>
                    <p className="text-sm text-[#A1A1AA] mb-6 max-w-md mx-auto">
                      Kliknij przycisk powyżej, aby wygenerować spersonalizowane rekomendacje
                      w oparciu o Twój profil muzyczny i AI w stylu Pandora.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#27272A] mt-12 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-[#71717A]">
            Pandora Vibes • Rekomendacje muzyczne w stylu Music Genome Project
          </p>
        </div>
      </footer>

      {/* Modal: szczegoly artysty (albumy, top utwory) */}
      <AnimatePresence>
        {artistDetail && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setArtistDetail(null)}
            className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#121212] border border-[#27272A] rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Avatar className="w-14 h-14">
                    <AvatarImage src={artistDetail.image} alt={artistDetail.name} />
                    <AvatarFallback className="bg-[#27272A]">{artistDetail.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{artistDetail.name}</h3>
                    {artistDetail.followers != null && (
                      <p className="text-xs text-[#A1A1AA]">{artistDetail.followers.toLocaleString('pl-PL')} obserwujących</p>
                    )}
                  </div>
                </div>
                <button onClick={() => setArtistDetail(null)} className="text-[#A1A1AA] hover:text-white text-xl leading-none">×</button>
              </div>

              {loadingArtistDetail ? (
                <div className="py-8 text-center">
                  <div className="w-8 h-8 border-2 border-[#FF3366] border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : (
                <>
                  {artistDetail.genres?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {artistDetail.genres.map((g, i) => (
                        <span key={i} className="genre-tag text-[10px]">{g}</span>
                      ))}
                    </div>
                  )}

                  {artistDetail.topTracks?.length > 0 && (
                    <div className="mb-4">
                      <p className="overline mb-2">Top utwory</p>
                      <div className="space-y-1">
                        {artistDetail.topTracks.slice(0, 5).map((t) => (
                          <a
                            key={t.id}
                            href={t.external_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm text-white hover:text-[#FF3366] transition-colors truncate"
                          >
                            <MusicNotes size={14} className="text-[#71717A] shrink-0" />
                            {t.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {artistDetail.albums?.length > 0 && (
                    <div>
                      <p className="overline mb-2">Albumy</p>
                      <div className="grid grid-cols-4 gap-2">
                        {artistDetail.albums.slice(0, 8).map((album) => (
                          <a key={album.id} href={album.external_url} target="_blank" rel="noopener noreferrer" title={album.name}>
                            <div className="aspect-square rounded overflow-hidden bg-[#1A1A1A]">
                              {album.image ? (
                                <img src={album.image} alt={album.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <MusicNotes size={16} className="text-[#3f3f46]" />
                                </div>
                              )}
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {artistDetail.external_url && (
                    <a
                      href={artistDetail.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 flex items-center justify-center gap-2 text-sm text-[#1DB954] hover:underline"
                    >
                      <SpotifyLogo size={16} /> Otwórz w Spotify
                    </a>
                  )}
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
